/**
 * Dice system — GDD §4.
 *
 * The design target is not "a random number". It is: the player should feel that
 * the roll was *theirs*. That comes from three things implemented here:
 *
 *  1. Result bands give a face meaning beyond its number (1 is content, not a
 *     dead turn — see `BAND_BY_FACE` and the failure hooks in encounter.ts).
 *  2. Manipulations let the player spend something to bend a result, so a bad
 *     roll is a decision point rather than an outcome.
 *  3. Fate Debt makes every manipulation cost something later, so bending fate
 *     is a strategy with a bill attached rather than a free undo.
 */

import type { DiceMaterial, DiceSkin, ResultBand } from './types.js';
import type { Rng } from './rng.js';

export type DiceFace = 1 | 2 | 3 | 4 | 5 | 6;

/** GDD §4: 1=failure/special, 2-3=ordinary, 4=strong, 5=skill boost, 6=critical. */
export const BAND_BY_FACE: Readonly<Record<DiceFace, ResultBand>> = {
  1: 'failure',
  2: 'ordinary',
  3: 'ordinary',
  4: 'strong',
  5: 'empowered',
  6: 'critical',
};

export function bandOf(face: DiceFace): ResultBand {
  return BAND_BY_FACE[face];
}

export type ManipulationKind =
  | 'reroll'
  | 'hold'
  | 'split'
  | 'double'
  | 'sacrifice'
  | 'gamble'
  | 'bank'
  | 'spend';

/**
 * Fate Debt charged per manipulation (GDD §4).
 * Tuned so that a player who manipulates every single roll reaches the first
 * threshold (25) in roughly 6-8 rolls — often enough to teach the mechanic
 * inside one 10-minute adventure, rarely enough not to feel punitive.
 */
export const FATE_DEBT_COST: Readonly<Record<ManipulationKind, number>> = {
  reroll: 4,
  hold: 2,
  split: 3,
  double: 6,
  sacrifice: 0, // you already paid, in the thing you gave up
  gamble: 5,
  bank: 3,
  spend: 0, // spending a banked die costs nothing extra; banking already charged
};

/** Fate Debt thresholds. Crossing one fires a Fate Event (GDD §4). */
export const FATE_DEBT_THRESHOLDS: readonly number[] = [25, 60, 110, 180] as const;

export const FATE_DEBT_MAX = 240;

/** Consecutive high results build a Fate Chain (GDD §4). */
export const FATE_CHAIN_MIN_FACE: DiceFace = 5;
export const FATE_CHAIN_TRIGGER = 3;

/** Consecutive low results open Bad Luck content instead of dead air (GDD §4). */
export const BAD_LUCK_MAX_FACE: DiceFace = 2;
export const BAD_LUCK_TRIGGER = 3;

/** A die the player owns. Cosmetic only — see the P2W note below. */
export interface DieDefinition {
  readonly id: string;
  readonly skin: DiceSkin;
  readonly material: DiceMaterial;
  readonly label: string;
  /**
   * GDD §26 / CLAUDE.md §4.6: paid dice sell appearance, animation and sound.
   * There is deliberately NO modifier/weight/luck field on this type, so a
   * pay-to-win die is not expressible in the data model, not merely discouraged.
   */
  readonly glow: 'none' | 'faint' | 'medium' | 'strong';
}

/** GDD §4 Dice Memory — "this is the die that killed the dragon". */
export interface DiceMemoryEntry {
  readonly dieId: string;
  readonly eventType: string;
  readonly summary: string;
  readonly tick: number;
  readonly rollSeq: number;
}

export interface RollRecord {
  readonly seq: number;
  readonly dieId: string;
  readonly material: DiceMaterial;
  /** Face as first rolled, before any manipulation. Kept for Chronicle honesty. */
  readonly naturalFace: DiceFace;
  /** Face after manipulations resolved. This is what the game acts on. */
  readonly finalFace: DiceFace;
  readonly band: ResultBand;
  readonly manipulations: readonly ManipulationKind[];
  /** Extra faces produced by SPLIT, if any. */
  readonly splitInto: readonly DiceFace[];
  readonly fateDebtAfter: number;
  readonly presentationSeed: number;
  readonly context: string;
  readonly tick: number;
}

export interface DiceState {
  /** Monotonic roll counter. Every consequence in the game can cite a rollSeq. */
  rollSeq: number;
  fateDebt: number;
  /** Thresholds already fired, so a Fate Event never double-fires. */
  fateDebtFired: number[];
  fateChain: number;
  badLuckStreak: number;
  /** GDD §4 Dice Bank: stored faces, spendable later. */
  bank: DiceFace[];
  bankCapacity: number;
  /** Manipulations the player has unlocked. GDD §30: reveal these gradually. */
  unlocked: ManipulationKind[];
  memories: DiceMemoryEntry[];
  /** Per-die roll tallies, for "this die has rolled 14 sixes" flavour. */
  faceTally: Record<string, number[]>;
}

export function createDiceState(
  unlocked: ManipulationKind[] = ['reroll', 'hold', 'split'],
): DiceState {
  return {
    rollSeq: 0,
    fateDebt: 0,
    fateDebtFired: [],
    fateChain: 0,
    badLuckStreak: 0,
    bank: [],
    bankCapacity: 3,
    unlocked: [...unlocked],
    memories: [],
    faceTally: {},
  };
}

export function isUnlocked(state: DiceState, kind: ManipulationKind): boolean {
  return state.unlocked.includes(kind);
}

export interface RollRequest {
  readonly die: DieDefinition;
  readonly context: string;
  readonly tick: number;
  /** Presentation-only seed, derived outside the gameplay streams. */
  readonly presentationSeed: number;
}

/** Roll one die. Mutates `state` counters but does not apply manipulations. */
export function rollDie(state: DiceState, rng: Rng, req: RollRequest): RollRecord {
  const seq = ++state.rollSeq;
  const face = rng.d6() as DiceFace;
  const tally = (state.faceTally[req.die.id] ??= [0, 0, 0, 0, 0, 0]);
  tally[face - 1] = (tally[face - 1] ?? 0) + 1;
  return {
    seq,
    dieId: req.die.id,
    material: req.die.material,
    naturalFace: face,
    finalFace: face,
    band: bandOf(face),
    manipulations: [],
    splitInto: [],
    fateDebtAfter: state.fateDebt,
    presentationSeed: req.presentationSeed,
    context: req.context,
    tick: req.tick,
  };
}

export interface ManipulationResult {
  readonly record: RollRecord;
  /** Fate Debt thresholds crossed by this manipulation, in ascending order. */
  readonly thresholdsCrossed: readonly number[];
  /** Present when SACRIFICE or GAMBLE demands the caller resolve a side effect. */
  readonly pending?: { readonly kind: 'sacrifice' | 'gamble'; readonly magnitude: number };
}

export class ManipulationError extends Error {
  constructor(
    message: string,
    readonly code: 'locked' | 'illegal' | 'exhausted',
  ) {
    super(message);
    this.name = 'ManipulationError';
  }
}

/**
 * 운명 빚을 더하고, 이번에 넘은 임계치를 돌려준다.
 *
 * 상한과 임계 부기는 **반드시 여기를 거쳐야 한다.** 인카운터 효과가
 * `state.dice.fateDebt += n` 으로 직접 더하던 경로가 있었는데, 그러면 상한을
 * 넘어서고 임계치도 안 터진다. 게다가 임계치를 조용히 건너뛴 다음부터는
 * `before < th` 조건이 영영 참이 될 수 없어 **그 임계치가 영구히 불발**된다.
 * 운명 빚은 "조작할수록 대가가 돌아온다"는 장치인데, 그 대가가 사라졌다는 뜻이다.
 */
export function addFateDebt(state: DiceState, amount: number): number[] {
  if (amount <= 0) return [];
  const before = state.fateDebt;
  state.fateDebt = Math.min(FATE_DEBT_MAX, before + amount);
  const crossed: number[] = [];
  for (const th of FATE_DEBT_THRESHOLDS) {
    if (before < th && state.fateDebt >= th && !state.fateDebtFired.includes(th)) {
      state.fateDebtFired.push(th);
      crossed.push(th);
    }
  }
  return crossed;
}

function chargeDebt(state: DiceState, kind: ManipulationKind): number[] {
  return addFateDebt(state, FATE_DEBT_COST[kind]);
}

function requireUnlocked(state: DiceState, kind: ManipulationKind): void {
  if (!isUnlocked(state, kind)) {
    throw new ManipulationError(`Manipulation '${kind}' is not unlocked yet`, 'locked');
  }
}

function withManipulation(
  record: RollRecord,
  kind: ManipulationKind,
  patch: Partial<RollRecord>,
): RollRecord {
  return {
    ...record,
    ...patch,
    manipulations: [...record.manipulations, kind],
  };
}

/** REROLL — discard the face and roll again. The natural face is preserved. */
export function reroll(state: DiceState, rng: Rng, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'reroll');
  const crossed = chargeDebt(state, 'reroll');
  return { ...applyReroll(state, rng, record), thresholdsCrossed: crossed };
}

/**
 * 빚 없이 다시 굴린다 (Fate Chain 이 준 무료 재굴림).
 *
 * 세션이 이 경우를 직접 손으로 조립하고 있었는데, 그때 `faceTally` 갱신과
 * `fateDebtAfter` 기록을 빠뜨렸다. 그래서 무료 재굴림으로 나온 눈은 Dice Memory
 * ("이 주사위로 무엇을 굴렸나")에 한 번도 집계되지 않았다 — 주사위가 자기
 * 역사를 기억하는 게임에서 기억에 구멍이 나 있었던 셈이다. 부기는 한 곳에 둔다.
 */
export function freeReroll(state: DiceState, rng: Rng, record: RollRecord): ManipulationResult {
  return { ...applyReroll(state, rng, record), thresholdsCrossed: [] };
}

function applyReroll(
  state: DiceState,
  rng: Rng,
  record: RollRecord,
): { record: RollRecord } {
  const face = rng.d6() as DiceFace;
  const tally = (state.faceTally[record.dieId] ??= [0, 0, 0, 0, 0, 0]);
  tally[face - 1] = (tally[face - 1] ?? 0) + 1;
  return {
    record: withManipulation(record, 'reroll', {
      finalFace: face,
      band: bandOf(face),
      fateDebtAfter: state.fateDebt,
    }),
  };
}

/** HOLD — preserve this result for a later roll in the same scene. */
export function hold(state: DiceState, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'hold');
  if (state.bank.length >= state.bankCapacity) {
    throw new ManipulationError('Dice Bank is full', 'exhausted');
  }
  const crossed = chargeDebt(state, 'hold');
  state.bank.push(record.finalFace);
  return {
    record: withManipulation(record, 'hold', { fateDebtAfter: state.fateDebt }),
    thresholdsCrossed: crossed,
  };
}

/**
 * SPLIT — divide an even face into two halves (6 -> 3+3, GDD §4).
 * Restricted to even faces >= 4: splitting a 2 into 1+1 would only ever hurt,
 * and letting the player do it by accident is a usability trap, not a choice.
 */
export function split(state: DiceState, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'split');
  const f = record.finalFace;
  if (f % 2 !== 0 || f < 4) {
    throw new ManipulationError(`Cannot split a ${f}; need an even face of 4 or more`, 'illegal');
  }
  const half = (f / 2) as DiceFace;
  const crossed = chargeDebt(state, 'split');
  return {
    record: withManipulation(record, 'split', {
      splitInto: [half, half],
      fateDebtAfter: state.fateDebt,
    }),
    thresholdsCrossed: crossed,
  };
}

/**
 * DOUBLE — double the result, capped at 6, and raise risk (GDD §4).
 * The risk is expressed as extra Fate Debt rather than a hidden penalty, so the
 * player can see the bill they are running up.
 */
export function double(state: DiceState, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'double');
  const crossed = chargeDebt(state, 'double');
  const doubled = Math.min(6, record.finalFace * 2) as DiceFace;
  return {
    record: withManipulation(record, 'double', {
      finalFace: doubled,
      band: bandOf(doubled),
      fateDebtAfter: state.fateDebt,
    }),
    thresholdsCrossed: crossed,
  };
}

/**
 * SACRIFICE — give up the current gain for a larger effect (GDD §4).
 * Costs no Fate Debt by design: the player already paid in the forfeited result,
 * and charging twice would make it strictly worse than REROLL.
 */
export function sacrifice(state: DiceState, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'sacrifice');
  const crossed = chargeDebt(state, 'sacrifice');
  return {
    record: withManipulation(record, 'sacrifice', {
      finalFace: 1,
      band: 'failure',
      fateDebtAfter: state.fateDebt,
    }),
    thresholdsCrossed: crossed,
    pending: { kind: 'sacrifice', magnitude: record.finalFace },
  };
}

/**
 * GAMBLE — reroll, keeping the new face whether better or worse, and the swing
 * is amplified: a 6 pays double, a 1 bites twice as hard (GDD §4).
 */
export function gamble(state: DiceState, rng: Rng, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'gamble');
  const crossed = chargeDebt(state, 'gamble');
  const face = rng.d6() as DiceFace;
  const tally = (state.faceTally[record.dieId] ??= [0, 0, 0, 0, 0, 0]);
  tally[face - 1] = (tally[face - 1] ?? 0) + 1;
  const magnitude = face >= 5 ? 2 : face <= 2 ? -2 : 1;
  return {
    record: withManipulation(record, 'gamble', {
      finalFace: face,
      band: bandOf(face),
      fateDebtAfter: state.fateDebt,
    }),
    thresholdsCrossed: crossed,
    pending: { kind: 'gamble', magnitude },
  };
}

/** BANK — store the result for later. Distinct from HOLD in that it persists across scenes. */
export function bank(state: DiceState, record: RollRecord): ManipulationResult {
  requireUnlocked(state, 'bank');
  if (state.bank.length >= state.bankCapacity) {
    throw new ManipulationError('Dice Bank is full', 'exhausted');
  }
  const crossed = chargeDebt(state, 'bank');
  state.bank.push(record.finalFace);
  return {
    record: withManipulation(record, 'bank', { fateDebtAfter: state.fateDebt }),
    thresholdsCrossed: crossed,
  };
}

/** Spend a banked face in place of rolling. */
export function spendBanked(state: DiceState, index: number): DiceFace {
  const face = state.bank[index];
  if (face === undefined) throw new ManipulationError('No banked die at that slot', 'illegal');
  state.bank.splice(index, 1);
  return face;
}

export interface StreakOutcome {
  readonly fateChainTriggered: boolean;
  readonly badLuckTriggered: boolean;
  readonly fateChainLength: number;
  readonly badLuckLength: number;
}

/**
 * Update streak counters once a roll is final. Call exactly once per roll,
 * after all manipulations, or Fate Chain will count the same roll twice.
 */
export function updateStreaks(state: DiceState, record: RollRecord): StreakOutcome {
  if (record.finalFace >= FATE_CHAIN_MIN_FACE) {
    state.fateChain += 1;
    state.badLuckStreak = 0;
  } else if (record.finalFace <= BAD_LUCK_MAX_FACE) {
    state.badLuckStreak += 1;
    state.fateChain = 0;
  } else {
    state.fateChain = 0;
    state.badLuckStreak = 0;
  }

  const fateChainTriggered = state.fateChain > 0 && state.fateChain % FATE_CHAIN_TRIGGER === 0;
  const badLuckTriggered = state.badLuckStreak > 0 && state.badLuckStreak % BAD_LUCK_TRIGGER === 0;

  return {
    fateChainTriggered,
    badLuckTriggered,
    fateChainLength: state.fateChain,
    badLuckLength: state.badLuckStreak,
  };
}

/** Attach a story to a die (GDD §4 Dice Memory). Capped to keep saves bounded. */
export const DICE_MEMORY_CAP = 24;

export function rememberWithDie(state: DiceState, entry: DiceMemoryEntry): void {
  state.memories.push(entry);
  if (state.memories.length > DICE_MEMORY_CAP) {
    state.memories.splice(0, state.memories.length - DICE_MEMORY_CAP);
  }
}

export function memoriesForDie(state: DiceState, dieId: string): DiceMemoryEntry[] {
  return state.memories.filter((m) => m.dieId === dieId);
}

/** How far along the player is toward the next Fate Event, for UI meters. */
export function fateDebtProgress(state: DiceState): {
  debt: number;
  nextThreshold: number | null;
  ratio: number;
} {
  const next = FATE_DEBT_THRESHOLDS.find((th) => state.fateDebt < th) ?? null;
  const prev = [...FATE_DEBT_THRESHOLDS].reverse().find((th) => state.fateDebt >= th) ?? 0;
  const ratio = next === null ? 1 : (state.fateDebt - prev) / (next - prev);
  return { debt: state.fateDebt, nextThreshold: next, ratio: Math.max(0, Math.min(1, ratio)) };
}

/** Fate Debt is paid down by consequence, never by a shop. */
export function relieveFateDebt(state: DiceState, amount: number): number {
  const before = state.fateDebt;
  state.fateDebt = Math.max(0, before - Math.max(0, amount));
  // Re-arm any threshold we have dropped back below, so it can fire again later.
  state.fateDebtFired = state.fateDebtFired.filter((th) => th <= state.fateDebt);
  return before - state.fateDebt;
}
